import { useContext, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { PaneCardContext } from './paneCardContext';

/**
 * phase-compiler-chrome-batch items 1 + 3 — THE pane-header treatment for the
 * Compiler's three panes (CTD Tree / Workspace / Inspector).
 *
 * Before this, the tree and the Inspector each carried a private copy of the
 * same title style while the centre pane had no title at all, and the pane
 * Hide buttons sat on their own row ABOVE the title — pushing it down a line.
 * One component now owns the geometry: title on the LEFT, any header action
 * (the Hide control) on the RIGHT, on ONE row.
 */

/** The shared title typography — identical across all three panes. Not
 *  exported (react-refresh single-export rule): consumers get it by
 *  rendering PaneHeaderRow, which is the point — the treatment cannot be
 *  copied out and drift. */
const PANE_TITLE_STYLE: CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--text)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

export interface PaneHeaderRowProps {
  readonly title: string;
  /** Distinguishes the three instances in tests. */
  readonly testId: string;
  /** Optional right-aligned control (the pane's Hide button). */
  readonly action?: ReactNode;
}

export function PaneHeaderRow({ title, testId, action }: PaneHeaderRowProps): ReactElement | null {
  const attachedTabs = useContext(PaneCardContext);
  if (attachedTabs) return null;
  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        margin: '0 0 4px 0',
      }}
    >
      <h2 style={PANE_TITLE_STYLE}>{title}</h2>
      {action ?? null}
    </div>
  );
}
